/**
 * EditContents component for managing and editing the table of contents of an asset.
 * Provides functionality to add, update, delete, and reorder sections and subsections.
 * Integrates drag-and-drop functionality using the `@dnd-kit` library.
 * Uses React's context API to manage state and actions related to the table of contents.
 * Updates the table of contents via Redux actions and API calls.
 * Styled using SCSS modules.
 *
 * @param {Function} handleClose - Callback function to close the table of contents editor.
 * @param {Object} asset - The asset object containing metadata and details about the asset.
 */

import _, { set } from "lodash";
import styles from "./styles.module.scss";
import cx from "classnames";

import Button from "src/components/button";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { guid } from "src/services/util";
import { useSpring, animated } from "@react-spring/web";
import useMeasure from "react-use-measure";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragOverlay,
  DragStartEvent,
  DragCancelEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { useDispatch } from "react-redux";

import { fetchTableOfContents, invalidateUpdateTableOfContents, updateTableOfContents as updateTableOfContensApi } from "src/redux/actions/assets"; 
import { useSelector } from "react-redux";
import WarningMessage from "src/components/warningMessage";

const emptyMessageImage = "/images/tableOfContents.svg";

interface Section {
  uid: string;
  name: string;
  page: number;
  sections: Section[];
  showChildren?: boolean;
}

interface CreateSection {
  uid: "create"
}

interface TocContextTypes {
  tableOfContents: (Section | CreateSection)[];
  addSection: () => void;
  createSectionNextTo: (id: string) => void;
  addSectionNextTo: (id: string) => void;
  updateSection: (id: string, action: SectionAction) => void;
  updateSubSection: (sectionId: string, subSectionId: string, action: SectionAction) => void;
  updateTableOfContents: (newContents: (Section | CreateSection)[]) => void;
  removeCreateSectionNextTo: () => void;
  addSubSection: (id: string) => void;
  totalPages: number;
  containsPageGreaterThanTotalPages: boolean
}


const TocContext = createContext<TocContextTypes>({
  tableOfContents: [],
  addSection: () => { },
  createSectionNextTo: (id: string) => { },
  addSectionNextTo: (id: string) => { },
  updateSection: () => { },
  updateSubSection: () => { },
  updateTableOfContents: () => { },
  removeCreateSectionNextTo: () => { },
  addSubSection: (id: string) => { },
  totalPages: 0,
  containsPageGreaterThanTotalPages: false
});

interface SectionAction {
  type: "name" | "page" | "show_children";
  id: string;
  payload?: any;
}

const EditContents = ({ handleClose, asset }) => {

  const tableOfContent = useSelector((state) => _.get(state, `asset.tableOfContents`,[]));
  
  const updateState = useSelector((state) => _.get(state, `asset.tableOfContents.update`, {}));
  const [tableOfContents, setTableOfContents] = useState<(Section | CreateSection)[]>([]);
  const isValueUpdated = useRef(false);
  const dispatch = useDispatch();

  const sections = _.get(tableOfContent, "sections", []);
  const updating = _.get(updateState, "loading", false);
  const updated = _.get(updateState, "succeeded", false);
  const fetching = _.get(tableOfContent, "loading", false);
  const fetched = _.get(tableOfContent, "succeeded", false);

  const flattenSections = useMemo(() => {
    let result: Section[] = [];

    const extractSections = (list) => {
      _.forEach(list, (section) => {
        section.uid != "create" && result.push(section); // Include all fields
        if (!_.isEmpty(section.sections)) {
          extractSections(section.sections);
        }
      });
    };

    extractSections(tableOfContents);
    return result;
  }, [tableOfContents]);

  const containsPageGreaterThanTotalPages = _.some(flattenSections, (section) => {
    return _.get(section,"page",0) > _.get(asset, "metadata.pages", 0);
  });

  const containsEmptyName = _.some(flattenSections, (section) => {
    return _.isEmpty(_.get(section, "name", ""));
  });

  const containsEmptyPage = _.some(flattenSections, (section) => {
    return _.get(section, "page", 0) <= 0;
  });

  useEffect(() => {
    if(fetched && !isValueUpdated.current){
      setTableOfContents(sections);
      isValueUpdated.current = true;
    }
  },[tableOfContent])

  useEffect(() => {
    if(!updating && updated){
      handleClose();
      dispatch(invalidateUpdateTableOfContents())
      dispatch(fetchTableOfContents({id: asset.id}));
    }
  },[updateState])

  const addSection = () => {
    const newSection = {
      uid: guid(),
      name: `Add Section`,
      page: 1,
      sections: [],
      showChildren: false,
    };
    const updatedContents = [...tableOfContents, newSection];
    setTableOfContents(updatedContents);
  };

  const createSectionNextTo = (id: string) => {

    const placeholderSection = {
      uid: "create"
    }

    const filteredContents = tableOfContents.filter((section) => section.uid !== "create");
    const index = filteredContents.findIndex((section) => section.uid === id);
    const newContents = [...filteredContents];
    newContents.splice(index + 1, 0, placeholderSection as CreateSection);

    setTableOfContents(newContents);
  }

  const updateSection = (id: string, action: SectionAction) => {
    const newContents = tableOfContents.map((section) => {
      if (section.uid !== id || !('name' in section || 'page' in section)) return section;
  
      if (action.type === "name") {
        return { ...section, name: action.payload };
      }
  
      if (action.type === "page") {
        return { ...section, page: action.payload };
      }
  
      if (action.type === "show_children") {
        return { ...section, showChildren: action.payload };
      }
  
      return section;
    });
  
    const finalContents =
      action.type === "show_children" && action.payload === true
        ? newContents.map((section) =>
            section.uid !== id && 'showChildren' in section
              ? { ...section, showChildren: false }
              : section
          )
        : newContents;
  
    setTableOfContents(finalContents);
  };
  

  const updateSubSection = (sectionId: string, subSectionId: string, action: SectionAction) => {
    const newContents = tableOfContents.map((section) => {
      if (section.uid !== sectionId || !('sections' in section)) return section;
  
      const updatedSections = section.sections.map((subSection) => {
        if (subSection.uid !== subSectionId) return subSection;
  
        if (action.type === "name") {
          return { ...subSection, name: action.payload };
        } else if (action.type === "page") {
          return { ...subSection, page: action.payload };
        }
  
        return subSection;
      });
  
      return { ...section, sections: updatedSections };
    });
  
    setTableOfContents(newContents);
  };


  const removeCreateSectionNextTo = () => {
    const filteredContents = tableOfContents.filter((section) => section.uid !== "create");
    setTableOfContents(filteredContents);
  }

  const updateTableOfContents = (newContents: (Section | CreateSection)[]) => {
    setTableOfContents(newContents);
  }

  const addSubSection = (id: string) => {

    const newSection = {
      uid: guid(),
      name: `Add Sub section`,
      page: 1,
      sections: [],
    };
    const newContents = [...tableOfContents];
    const index = newContents.findIndex((section) => section.uid === id);
    if (index !== -1) {
      // (newContents[index] as Section).sections.push(newSection);
      const updatedSections = {
        ...newContents[index],
        sections: [...(newContents[index] as Section).sections, newSection],
      };
      newContents[index] = updatedSections;
    }

    setTableOfContents(newContents);
    
  }

  const addSectionNextTo = (id: string) => {
    const newSection = {
      uid: guid(),
      name: `New Section ${_.size(tableOfContents) + 1}`,
      page: 1,
      sections: [],
      showChildren: false,
    };
    const filteredContents = tableOfContents;
    const index = filteredContents.findIndex((section) => section.uid === id);
    const newContents = [...filteredContents];
    newContents.splice(index + 1, 0, newSection);
    setTableOfContents(newContents);
  }

  const handleUpdateTableOfContents = () => {
    const filtertedContents = tableOfContents.filter((section) => section.uid !== "create");
    const updatedContents = filtertedContents.map((section) => {
      if ('showChildren' in section) {
        const { showChildren, ...rest } = section;
        return rest;
      }
      return section;
    });

    dispatch(updateTableOfContensApi({
      assetId: asset.id,
      request:{
        sections: updatedContents as Section[]
      }
    }))

  }

  const getWarningContent = useMemo(() => {
    const messages : string[] = [];
    if((containsPageGreaterThanTotalPages || containsEmptyPage) && containsEmptyName) {
      messages.push("Please make sure all entries have a name and a valid page number.");
    }
    else if (containsPageGreaterThanTotalPages || containsEmptyPage) {
      messages.push(`Please enter a page number within the document's 1-${_.get(asset, "metadata.pages", 0)} page range.`);
    }
    else if (containsEmptyName) {
      messages.push("Please provide a valid name before saving.");
    }
    return messages.join(" ");
  }, [containsPageGreaterThanTotalPages, containsEmptyName, containsEmptyPage]);

  const disableSave = containsPageGreaterThanTotalPages || containsEmptyName || containsEmptyPage;

  return (
    <TocContext.Provider value={{ addSection, totalPages : _.get(asset,"metadata.pages",0), createSectionNextTo,addSectionNextTo, tableOfContents, updateSection, updateTableOfContents, removeCreateSectionNextTo, addSubSection, updateSubSection, containsPageGreaterThanTotalPages }}>
      <div className={styles.tableOfContents}>
        <p className={styles.title}>
          edit table of contents <i onClick={handleClose} className="paperflite-close" />
        </p>
        {_.size(tableOfContents) === 0 ? (
          <div className={styles.emptyMessage}>
            <div>
              <img src={emptyMessageImage} />
              <p>no table of contents found</p>
              <Button
                variant="primary"
                className={styles.saveButton}
                icon="paperflite-tick"
                text={"add content"}
                disabled={false}
                onClick={addSection}
              />
            </div>
          </div>
        ) : (
          <SectionBrowser data={tableOfContents} />
        )}

       {disableSave && <WarningMessage
          type="WARNING"
          header="psst!"
          className={styles.warningMessage}
          message={getWarningContent}
        />}

        <div className={styles.buttonContainer}>
          <Button
            variant="primary"
            className={styles.saveButton}
            icon="paperflite-tick"
            text={updating ? "Updating..." : "Update"}
            disabled={ disableSave || updating}
            onClick={handleUpdateTableOfContents}
          />
          <Button
            variant="secondary"
            className={styles.cancelButton}
            onClick={handleClose}
            text="Cancel"
          />
        </div>
      </div>
    </TocContext.Provider>
  );
};

const SectionBrowser = ({ data }) => {

  const { addSection, tableOfContents, updateTableOfContents, removeCreateSectionNextTo, containsPageGreaterThanTotalPages } = useContext(TocContext);

  const isCreateSectionExits = _.some(data, (section) => section?.uid === "create");
  const sensors = useSensors(useSensor(PointerSensor));
  const [activeDragSection, setActiveDragSection] = useState<any>(null);
  const handleDragStart = (event: DragStartEvent) => {
    removeCreateSectionNextTo();
    const { active } = event;
    const activeSection = _.find(tableOfContents, (item) => _.get(item, 'uid') === active.id);
    setActiveDragSection(activeSection);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const oldIndex = _.findIndex(tableOfContents, (item) => _.get(item, 'uid') === active.id);
    const newIndex = _.findIndex(tableOfContents, (item) => _.get(item, 'uid') === _.get(over, "id", ""));
    const updatedArray = arrayMove(tableOfContents, oldIndex, newIndex);
    updateTableOfContents(updatedArray);
    setActiveDragSection(null);
  };

  const handleDragCancel = (event: DragCancelEvent) => {
    setActiveDragSection(null);
  };

  return (
    <div className={cx(styles.sections,{
      [styles.exceeded]: containsPageGreaterThanTotalPages
    })}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={data.map((item) => item.uid)}
          strategy={verticalListSortingStrategy}
        >

          {_.size(data) > 0 &&
            _.map(data, (section, index) => <SectionEntity key={section.uid} index={index} section={section} />)}

          {!isCreateSectionExits && <div className={styles.addContent} onClick={addSection}>
            <i className="paperflite-add" /> add content
          </div>}
        </SortableContext>
        <DragOverlay modifiers={[restrictToWindowEdges]}>
          {activeDragSection && <SectionEntity section={activeDragSection} index={-1} />}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

const SectionEntity = ({ section, index }) => {

  const [containerRef, containerBounds] = useMeasure();
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pageNumberRef = useRef<HTMLInputElement | null>(null);
  const sortRef = useRef<HTMLDivElement | null>(null);
  const { tableOfContents,updateTableOfContents, createSectionNextTo,totalPages, addSectionNextTo, addSubSection, addSection, updateSection, removeCreateSectionNextTo } = useContext(TocContext);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showChildren = _.get(section,'showChildren', false);
  const [childrenAnimation] = useSpring(
    {
      opacity: showChildren ? 1 : 0,
      height: showChildren ? containerBounds.height : 0,
      from: { opacity: 0, height: 0 },
    },
    [showChildren, containerBounds, editing],
  );

  const handleDropdownClick = () => {
    updateSection(section.uid, { type: "show_children", id: section.uid, payload: !showChildren });
  }

  useEffect(() => {
    if (editing) {
      if(_.size(section.sections) === 0){
        updateSection(section.uid, { type: "show_children", id: section.uid, payload: false });
      }
      removeCreateSectionNextTo()
    }
  }, [editing])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        (inputRef.current && !inputRef.current.contains(event.target as Node)) &&
        (pageNumberRef.current && !pageNumberRef.current.contains(event.target as Node)) && 
        (sortRef.current && !sortRef.current.contains(event.target as Node))
      ) {
        setEditing(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleClick = () => {
    if (clickTimeoutRef.current === null) {
      clickTimeoutRef.current = setTimeout(() => {
        createSectionNextTo(section.uid);
        clickTimeoutRef.current = null;
      }, 300);
    }
  };


  const handleDoubleClick = () => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    setEditing(true);
  };

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: section.uid });

  const transformStyles = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };


  const handleNameChange = (e) => {
    const name = e.target.value;
    updateSection(section.uid, { type: "name", id: section.uid, payload: name });
  };

  const handlePageChange = (e) => {
    const value = _.get(e,"target.value", e);
    updateSection(section.uid, { type: "page", id: section.uid, payload: value });
  };

  const handleSectionDelete = (e) => {
    e.stopPropagation();
    const filteredContents = tableOfContents.filter((item) => item.uid !== section.uid);
    const contentsWithCreateSection = filteredContents.filter((item) => item.uid != "create");
    updateTableOfContents(contentsWithCreateSection);
  }

  if (section.uid === "create") {
    return (
      <div className={styles.addContent} onClick={() => addSectionNextTo(section.uid)}>
        <i className="paperflite-add" /> add  content
      </div>
    ) 
  }

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
      }}
      style={transformStyles}
      >
        <div className={cx(styles.section, {
        [styles.editSection]: editing,
      })}>

        <div className={styles.staticIcons}>
        <i className={cx("paperflite-drag", styles.dragIcon)}  {...attributes} {...listeners} />

        <i
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownClick();
          }}
          className={cx("paperflite-dropdown", styles.dropdown, {

            [styles.rotate]: showChildren,
          })}
        />
        </div>

        {editing ? <div className={cx(styles.inputContainer,{
          [styles.warning]: editing && section.page > totalPages,
        })}>
          <input onChange={handleNameChange} ref={inputRef} autoFocus className={styles.name} type="text" value={section.name} />
          <input onChange={handlePageChange} min={1} ref={pageNumberRef} className={styles.pageNumber} type="number" value={section.page}></input>
          <div ref={sortRef} className={styles.sortContainer}>
            <SortIcons onUp={() => {
              handlePageChange(section.page + 1);
            }} onDown={() => {
              handlePageChange(section.page - 1 < 1 ? 1 : section.page - 1);
            }}
            />
          </div>
        </div> :
        <div className={styles.inner} onClick={handleClick} onDoubleClick={handleDoubleClick}>
          <div className={styles.left}>
           
            <span className={styles.name}> {section.name} </span>
          </div>
          <span className={styles.pageNumber}>
            <span className={styles.page}>{section.page}</span>
            <span className={styles.deleteIcon}>
              <i onClick={handleSectionDelete} className={cx("paperflite-delete")} />
            </span>
          </span>
        </div>}
          {section.page > totalPages && <span className={styles.warningDot}/>}
        </div>

      <animated.div style={childrenAnimation} className={styles.children}>
        <div ref={containerRef} className={styles.subSectionContainer} >

          {_.size(section.sections) > 0 &&
            _.map(section.sections, (item, index) => (
                <SubSection key={section.uid} parentSection={section} section={item} index={index} />
            ))}
          <div className={styles.addSubContent} onClick={() => addSubSection(section.uid)}>
            <i className="paperflite-arrow-bend-down-right" /> add sub content
          </div>
        </div>

      </animated.div>
    </div>
  );
};

const SubSection = ({ section, parentSection, index }) => {

  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pageNumberRef = useRef<HTMLInputElement | null>(null);
  const sortRef = useRef<HTMLDivElement | null>(null);
  
  const { updateSubSection, updateTableOfContents, tableOfContents, totalPages } = useContext(TocContext);
  const parentId = parentSection.uid;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        (inputRef.current && !inputRef.current.contains(event.target as Node)) &&
        (pageNumberRef.current && !pageNumberRef.current.contains(event.target as Node)) &&
        (sortRef.current && !sortRef.current.contains(event.target as Node))
      ) {
        setEditing(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  const handleDoubleClick = () => {
    setEditing(true);
  };


  const handleNameChange = (e) => {
    const name = e.target.value;
    updateSubSection(parentId, section.uid, { type: "name", id: section.uid, payload: name });
  };

  const handlePageChange = (e) => {
    const value = _.get(e,"target.value", e);
    updateSubSection(parentId, section.uid, { type: "page", id: section.uid, payload: value });
  };

  const handleDelete = () => {
    const updatedSection = _.get(parentSection, 'sections', []).filter((item) => item.uid !== section.uid);
    const updatedParentSection = {
      ...parentSection,
      sections: updatedSection,
    };
    const updatedTableOfContents = _.map(tableOfContents, (item) => {
      if (item.uid === parentId) {
        return updatedParentSection;
      }
      return item;
    });
    updateTableOfContents(updatedTableOfContents);
    
  }

  return (
    <div className={cx(styles.subSection, {
      [styles.editSection]: editing,
    })}>
      {section.page > totalPages && <span className={styles.warningDot}/>}

      {editing ? <div className={cx(styles.inputContainer,{
          [styles.warning]: editing && section.page > totalPages,
      })}>
        <input onChange={handleNameChange} ref={inputRef} autoFocus className={styles.name} type="text" value={section.name} />
        <input onChange={handlePageChange} ref={pageNumberRef}  className={styles.pageNumber} type="number" value={section.page} />
        <div ref={sortRef} className={styles.sortContainer}>
          <SortIcons onUp={() => {
            handlePageChange(section.page + 1);
          }} onDown={() => {
            handlePageChange(section.page - 1 < 1 ? 1 : section.page - 1);
          }}/>
        </div>
      </div> :
        <div className={styles.inner}  onDoubleClick={handleDoubleClick}>
          <div className={styles.left}>
            <span className={styles.name}> {section.name} </span>
          </div>
          <span className={styles.pageNumber}>
            <span className={styles.subSectionpage}>{section.page}</span>
            <span className={styles.subSectionDeleteIcon}>
              <i onClick={handleDelete} className={cx("paperflite-delete")} />
            </span>
          </span>
        </div>}
    </div>
  )
}

const SortIcons = ({onUp, onDown}) => {
  return(
    <div className={styles.sortIconsContainer}>
    <i onClick={onUp} className={cx(styles.up, "paperflite-dropdown")} />
    <i onClick={onDown} className={cx(styles.down, "paperflite-dropdown")} />
  </div>
  )
}


export default EditContents;
